import { useMemo, useRef, useState } from "react"
import {
  ColumnOrderState,
  ColumnPinningState,
  GroupingState,
  RowSelectionState,
  SortingState,
  VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  /*   getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel, */
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  getAllLeafColumnDefs,
  getColumnId,
  getDefaultColumnFilterFn,
  getDefaultColumnPinning,
  prepareColumn,
} from "@common/table/column.util"
import { useDisplayColumns } from "@common/table/hooks/useDisplayColumns"
import { mergeObjects } from "@common/lib/util"
import { RTable, TableOptions } from "@common/table/types"
import { getEditableTableState, getRowSelections } from "@common/table/util"

export const useTableInstance = (tableOptions: TableOptions) => {
  const {
    data = [],
    initialState,
    enablePagination,
    manualPagination,
    columns,
    tableRef,
    tableHeaderRef,
    topToolbarRef,
    tableContainerRef,
    enableExpanding,
    enableGrouping,
    enableFacetedValues,
    enableColumnFilters,
    enableGlobalFilter,
    enableFilters,
    enableSorting,
    enableColumnOrdering,
    enableColumnVisibility,
    // enableColumnResizing,
    enableRowSelection,
    enablePinning,
    filterFns,
    pagination,
  } = tableOptions

  const searchInputRef = useRef(null)
  const [expanded, setExpanded] = useState(initialState?.expanded ?? {})
  const [sorting, setSorting] = useState<SortingState>(initialState?.sorting ?? [])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    initialState?.columnVisibility ?? {}
  )
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(initialState?.selectedRowIds ? getRowSelections(data, initialState.selectedRowIds) : {})
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([])
  /*   const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
    const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>({} as ColumnSizingInfoState)
    console.log('columnSizingInfo', columnSizingInfo)
    console.log('columnSizing', columnSizing) */
  const [grouping, setGrouping] = useState<GroupingState>(
   /*  initialState?.grouping ?? */[]

  )
  const mergedPinning = mergeObjects(
    getDefaultColumnPinning(tableOptions),
    initialState?.columnPinning
  ) as {
    left: Array<string>
    right: Array<string>
  }
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    ...mergedPinning,
    right: mergedPinning.right.reverse(),
  })
  const isServerPagination = enablePagination && manualPagination

  /*   const [draggingColumn, setDraggingColumn] = useState(
      initialState.draggingColumn ?? null
    )
    const [draggingRow, setDraggingRow] = useState(initialState.draggingRow ?? null)
    const [globalFilterFn, setGlobalFilterFn] = useState(
      initialState.globalFilterFn ?? "fuzzy"
    )
    const [editingCell, setEditingCell] = useState(initialState.editingCell ?? null)
    const [density, setDensity] = useState(initialState?.density ?? "comfortable")
    const [editingRow, setEditingRow] = useState(initialState.editingRow ?? null) * /
    const [hoveredColumn, setHoveredColumn] = useState(
      initialState.hoveredColumn ?? null
    )
    const [hoveredRow, setHoveredRow] = useState(initialState.hoveredRow ?? null)
  
     */

  const [_columnFilterFns, setColumnFilterFns] = useState(() =>
    Object.assign(
      {},
      ...getAllLeafColumnDefs(columns).map((col) => ({
        [getColumnId(col)]:
          col.filterFn instanceof Function
            ? col.filterFn.name ?? "custom"
            : col.filterFn ??
            // initialState?.columnFilterFns?.[getColumnId(col)] ??
            getDefaultColumnFilterFn(col),
      }))
    )
  )
  const [globalFilterFn, _setGlobalFilterFn] = useState("fuzzy")


  const displayColumns = useDisplayColumns({
    tableOptions,
    grouping,
  })
  const rowActions = displayColumns.find((col) => col.id === "row-actions")

  const columnDefs = useMemo(
    () => [
      ...(displayColumns.filter((col) => col.id !== "row-actions") || []),
      ...prepareColumn({ columns }),
      ...(rowActions ? [rowActions] : []),
    ],
    [columns, displayColumns, rowActions]
  )
  const state = { columnVisibility, columnPinning, grouping, expanded, sorting, columnOrder }

  const createHandler = (setterFunction: any, isEnabled: boolean | undefined, stateKey: string) => (updater: any) => {
    const _newStateProp = functionalUpdate(updater, (state as any)[stateKey])
    const newState = {
      ...state,
      [stateKey]: _newStateProp
    }
    tableOptions.onTableStateChange?.(getEditableTableState(newState))
    isEnabled ? setterFunction(updater) : undefined
  }

  const handleColumnVisibilityChange = createHandler(setColumnVisibility, enableColumnVisibility, 'columnVisibility')
  const handleSortingChange = createHandler(setSorting, enableSorting, 'sorting')
  const handleColumnPinningChange = createHandler(setColumnPinning, enablePinning, 'columnPinning')

  const table = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel:
      enableExpanding || enableGrouping ? getExpandedRowModel() : undefined,
    getFacetedMinMaxValues: enableFacetedValues
      ? getFacetedMinMaxValues()
      : undefined,
    getFacetedRowModel: enableFacetedValues ? getFacetedRowModel() : undefined,
    getFacetedUniqueValues: enableFacetedValues
      ? getFacetedUniqueValues()
      : undefined,
    getFilteredRowModel:
      enableColumnFilters || enableGlobalFilter || enableFilters
        ? getFilteredRowModel()
        : undefined,
    getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    onColumnOrderChange: enableColumnOrdering ? setColumnOrder : undefined,
    onGroupingChange: enableGrouping ? setGrouping : undefined,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onExpandedChange: enableExpanding ? setExpanded : undefined,
    onSortingChange: handleSortingChange,
    onRowSelectionChange: enableRowSelection ? setRowSelection : undefined,
    onColumnPinningChange: handleColumnPinningChange,
    /*     onColumnSizingChange: enableColumnResizing ? setColumnSizing : undefined,
        onColumnSizingInfoChange: enableColumnResizing ? setColumnSizingInfo : undefined, */
    onColumnFiltersChange: enableColumnFilters ? setColumnFilterFns : undefined,
    getColumnCanGlobalFilter: () => enableGlobalFilter ? true : false,
    getSubRows: (row) => row.subRows,
    ...tableOptions,
    columns: columnDefs,
    data,
    globalFilterFn: filterFns?.[globalFilterFn ?? "fuzzy"],
    columnResizeMode: "onChange",
    state: {
      columnOrder,
      expanded,
      grouping,
      // columnFilterFns,
      sorting,
      columnVisibility,
      rowSelection,
      // globalFilterFn,
      /*       columnSizing,
            columnSizingInfo, */
      columnPinning,
      pagination: isServerPagination ? pagination : undefined,
    },
  }) as RTable

  table.refs = {
    tableRef,
    tableHeaderRef,
    topToolbarRef,
    tableContainerRef,
    searchInputRef,
    /*
    bottomToolbarRef,
    editInputRefs,
    filterInputRefs,
    searchInputRef,
    tableContainerRef,
    tableHeadCellRefs,
    tablePaperRef,
    topToolbarRef, 
    */
  }

  return table
}
